import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { AppKey } from '@/core/layout/app-registry';

/**
 * How the motif is sized.
 *
 * - `full`    — the 520×72 artwork from the board.
 * - `compact` — the same artwork shrunk and faded for narrow mastheads.
 * - `auto`    — `full`, switching to `compact` through the masthead's
 *   container query (`@container masthead (max-width: 1024px)`), with a
 *   further "mini" tier at ≤ 480px. Pure CSS: no JS, no resize observer.
 */
export type AppMotifVariant = 'full' | 'compact' | 'auto';

/** The artwork's intrinsic viewBox: every motif is drawn on this 520×72 grid. */
export const APP_MOTIF_VIEWBOX = Object.freeze({ width: 520, height: 72 });

/**
 * Workspace Management grid: 3 rows × 21 columns of 14px squares with a 7px
 * gap, right-aligned 24px in from the box edge and vertically centred. The
 * fill rule is ported verbatim from the board
 * (`docs/design/app-headers/boards/N8-Combined.dc.html`): nothing is filled
 * in the first seven columns, then the density grows towards the right.
 */
export interface AppMotifGridCell {
  readonly x: number;
  readonly y: number;
  readonly filled: boolean;
}

const GRID_ROWS = 3;
const GRID_COLUMNS = 21;
const GRID_CELL = 14;
const GRID_GAP = 7;
const GRID_PITCH = GRID_CELL + GRID_GAP;
const GRID_WIDTH = GRID_COLUMNS * GRID_CELL + (GRID_COLUMNS - 1) * GRID_GAP; // 434
const GRID_HEIGHT = GRID_ROWS * GRID_CELL + (GRID_ROWS - 1) * GRID_GAP; // 56
const GRID_INSET_RIGHT = 24;
const GRID_LEFT = APP_MOTIF_VIEWBOX.width - GRID_INSET_RIGHT - GRID_WIDTH; // 62
const GRID_TOP = (APP_MOTIF_VIEWBOX.height - GRID_HEIGHT) / 2; // 8

export const APP_MOTIF_GRID_CELLS: readonly AppMotifGridCell[] = (() => {
  const cells: AppMotifGridCell[] = [];
  for (let r = 0; r < GRID_ROWS; r++) {
    for (let c = 0; c < GRID_COLUMNS; c++) {
      const filled = c > 6 && (c * 7 + r * 3) % 5 < Math.min(4, Math.floor((c - 4) / 4));
      cells.push({ x: GRID_LEFT + c * GRID_PITCH, y: GRID_TOP + r * GRID_PITCH, filled });
    }
  }
  return cells;
})();

/** Side of one grid square, and the corner radius the board gives it. */
export const APP_MOTIF_GRID_CELL_SIZE = GRID_CELL;
export const APP_MOTIF_GRID_CELL_RADIUS = 3;

/**
 * Dashboards bar rhythm: the thirty bar heights from the board, verbatim.
 * Bars are 7px wide with a 7px gap on a 2px baseline, right-aligned 24px in
 * from the box edge; every sixth bar is the stronger grey.
 */
export const APP_MOTIF_BAR_HEIGHTS: readonly number[] = [
  16, 22, 19, 28, 25, 34, 30, 38, 32, 27, 36, 42, 37, 46, 40, 31, 37, 44, 48, 43, 51, 47, 41, 50, 54, 48, 52, 56, 50, 54,
];

export interface AppMotifBar {
  readonly x: number;
  readonly y: number;
  readonly height: number;
  readonly strong: boolean;
}

const BAR_WIDTH = 7;
const BAR_GAP = 7;
const BAR_PITCH = BAR_WIDTH + BAR_GAP;
const BAR_BASELINE_STROKE = 2;
const BAR_AREA_HEIGHT = 56; // the board's bar container; bars grow up from its bottom edge
const BAR_INSET_RIGHT = 24;
const BARS_WIDTH = APP_MOTIF_BAR_HEIGHTS.length * BAR_WIDTH + (APP_MOTIF_BAR_HEIGHTS.length - 1) * BAR_GAP; // 413
const BARS_LEFT = APP_MOTIF_VIEWBOX.width - BAR_INSET_RIGHT - BARS_WIDTH; // 83
const BARS_TOP = (APP_MOTIF_VIEWBOX.height - BAR_AREA_HEIGHT - BAR_BASELINE_STROKE) / 2; // 7
/** y of the top of the 2px baseline; bars end here. */
const BAR_BASELINE_Y = BARS_TOP + BAR_AREA_HEIGHT; // 63

export const APP_MOTIF_BARS: readonly AppMotifBar[] = APP_MOTIF_BAR_HEIGHTS.map((height, i) => ({
  x: BARS_LEFT + i * BAR_PITCH,
  y: BAR_BASELINE_Y - height,
  height,
  strong: i % 6 === 5,
}));

/** Geometry of the bars' baseline, exported for the template and the spec. */
export const APP_MOTIF_BAR_GEOMETRY = Object.freeze({
  width: BAR_WIDTH,
  radius: 2,
  baselineY: BAR_BASELINE_Y + BAR_BASELINE_STROKE / 2,
  baselineX1: BARS_LEFT,
  baselineX2: BARS_LEFT + BARS_WIDTH,
  baselineStroke: BAR_BASELINE_STROKE,
});

/**
 * **App motif** — the decorative grey artwork drawn top-right in an app's
 * masthead: a funnel converging into an arrow (Opportunity Management), a
 * dashed walking route with three stops (Gemba), a 5S grid filling in
 * (Workspace Management) and a bar rhythm (Dashboards). Ported from
 * `docs/design/app-headers/boards/N8-Combined.dc.html`.
 *
 * Purely decorative: the host is `aria-hidden`, has no text, no colour and
 * never carries information. Ink is limited to three greys defined once as
 * custom properties at the top of `app-motif.component.scss`
 * (`--motif-ink`, `--motif-ink-soft`, `--motif-ink-faint`).
 *
 * Every motif is an inline SVG on the same 520×72 grid with
 * `preserveAspectRatio="xMaxYMid meet"`, so the compact variant is a scale
 * that stays pinned to the right edge. The `variant` input is reflected as a
 * `data-variant` attribute and a `motif--<variant>` class; `auto` (the
 * default) lets the masthead's container query drive the switch.
 *
 * ```html
 * <app-masthead [app]="app" [tabs]="tabs" [activeTabKey]="active">
 *   <app-motif masthead-motif [app]="app.key" />
 * </app-masthead>
 * ```
 */
@Component({
  standalone: true,
  selector: 'app-motif',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    'aria-hidden': 'true',
    '[attr.data-variant]': 'variant()',
    '[class]': "'motif--' + variant()",
  },
  template: `
    @switch (app()) {
      @case ('om') {
        <!-- Funnel: ten nodes converge on one circle, which points onward. -->
        <svg class="motif__svg" data-motif="om" viewBox="0 0 520 72" preserveAspectRatio="xMaxYMid meet" focusable="false">
          <g class="motif__ink-soft" stroke-width="1">
            <path d="M40 12 380 36" />
            <path d="M70 40 380 36" />
            <path d="M30 60 380 36" />
            <path d="M100 22 380 36" />
            <path d="M120 54 380 36" />
            <path d="M160 8 380 36" />
            <path d="M170 38 380 36" />
            <path d="M200 64 380 36" />
            <path d="M230 26 380 36" />
            <path d="M250 50 380 36" />
          </g>
          <g class="motif__paper" stroke-width="1.5">
            <circle cx="40" cy="12" r="3.5" />
            <circle cx="70" cy="40" r="3.5" />
            <circle cx="30" cy="60" r="3.5" />
            <circle cx="100" cy="22" r="3.5" />
            <circle cx="120" cy="54" r="3.5" />
            <circle cx="160" cy="8" r="3.5" />
            <circle cx="170" cy="38" r="3.5" />
            <circle cx="200" cy="64" r="3.5" />
            <circle cx="230" cy="26" r="3.5" />
            <circle cx="250" cy="50" r="3.5" />
          </g>
          <circle class="motif__paper" cx="380" cy="36" r="6" stroke-width="2" />
          <path d="M388 36h100" stroke-width="2.5" />
          <path d="m478 27 12 9-12 9" stroke-width="2.5" />
        </svg>
      }
      @case ('gemba') {
        <!-- Walking route: a dotted path with three stops over two faint contour lines. -->
        <svg class="motif__svg" data-motif="gemba" viewBox="0 0 520 72" preserveAspectRatio="xMaxYMid meet" focusable="false">
          <path class="motif__ink-faint" d="M0 66C120 46 200 70 320 58S460 46 520 64" stroke-width="1" />
          <path class="motif__ink-faint" d="M0 8C100 20 220 0 340 10S470 20 520 4" stroke-width="1" />
          <path d="M14 54C74 54 80 18 140 20S210 58 270 48 330 14 390 22 470 52 506 38" stroke-width="3" stroke-dasharray="0.5 9" />
          <circle class="motif__paper" cx="14" cy="54" r="3.5" stroke-width="2" />
          <g class="motif__paper" stroke-width="2">
            <circle cx="140" cy="20" r="7" />
            <circle cx="270" cy="48" r="7" />
            <circle cx="390" cy="22" r="7" />
          </g>
          <g class="motif__fill">
            <circle cx="140" cy="20" r="2.5" />
            <circle cx="270" cy="48" r="2.5" />
            <circle cx="390" cy="22" r="2.5" />
          </g>
          <path d="m498 30 10 8-10 8" stroke-width="2.5" />
        </svg>
      }
      @case ('wm') {
        <!-- 5S grid: 3×21 squares, filling in from left to right. -->
        <svg class="motif__svg" data-motif="wm" viewBox="0 0 520 72" preserveAspectRatio="xMaxYMid meet" focusable="false">
          @for (cell of gridCells; track $index) {
            @if (cell.filled) {
              <svg:rect
                class="motif__fill-soft"
                [attr.x]="cell.x"
                [attr.y]="cell.y"
                [attr.width]="gridCellSize"
                [attr.height]="gridCellSize"
                [attr.rx]="gridCellRadius"
              />
            } @else {
              <svg:rect
                class="motif__ink-soft"
                stroke-width="1.5"
                [attr.x]="cell.x + 0.75"
                [attr.y]="cell.y + 0.75"
                [attr.width]="gridCellSize - 1.5"
                [attr.height]="gridCellSize - 1.5"
                [attr.rx]="gridCellRadius - 0.75"
              />
            }
          }
        </svg>
      }
      @case ('dash') {
        <!-- Bar rhythm: thirty bars on a baseline, every sixth one stronger. -->
        <svg class="motif__svg" data-motif="dash" viewBox="0 0 520 72" preserveAspectRatio="xMaxYMid meet" focusable="false">
          @for (bar of bars; track $index) {
            <svg:rect
              [class]="bar.strong ? 'motif__fill' : 'motif__fill-faint'"
              [attr.x]="bar.x"
              [attr.y]="bar.y"
              [attr.width]="barGeometry.width"
              [attr.height]="bar.height + barGeometry.baselineStroke"
              [attr.rx]="barGeometry.radius"
            />
          }
          <svg:path
            class="motif__ink-soft"
            [attr.d]="'M' + barGeometry.baselineX1 + ' ' + barGeometry.baselineY + 'H' + barGeometry.baselineX2"
            [attr.stroke-width]="barGeometry.baselineStroke"
            stroke-linecap="butt"
          />
        </svg>
      }
    }
  `,
  styleUrl: './app-motif.component.scss',
})
export class AppMotifComponent {
  /** Which application's motif to draw. */
  readonly app = input.required<AppKey>();
  /** Sizing: `full`, `compact`, or `auto` (container-query driven, the default). */
  readonly variant = input<AppMotifVariant>('auto');

  protected readonly gridCells = APP_MOTIF_GRID_CELLS;
  protected readonly gridCellSize = APP_MOTIF_GRID_CELL_SIZE;
  protected readonly gridCellRadius = APP_MOTIF_GRID_CELL_RADIUS;
  protected readonly bars = APP_MOTIF_BARS;
  protected readonly barGeometry = APP_MOTIF_BAR_GEOMETRY;
}
